import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import WarningIcon from '@mui/icons-material/Warning';
import { Palette, PaletteColor, Skeleton, alpha } from '@mui/material';
import MuiChip, { ChipProps } from '@mui/material/Chip';
import { forwardRef } from 'react';
import { useTheme } from 'styled-components';
import { Icon } from '../icon/index';

export interface ExtendedChipProps extends ChipProps {
	/**
	 * Provides the `Chip` with a maximum length, and cuts the label off with an ellipses
	 */
	maxWidth?: string | number | undefined;
	center?: boolean;
}

type ValidKeys<T, TValue> = {
	[K in keyof T]: T[K] extends TValue ? K : never;
}[keyof T];

export type PaletteColors = ValidKeys<Palette, PaletteColor>;

export interface IStatusChip extends Omit<ExtendedChipProps, 'title'> {
	backgroundColor?: PaletteColors;
	label: string;
	title?: string | false;
}

/**
 * The base Chip component is a compact, inline element for displaying short, contextual information such as tags, categories, or statuses.
 * Highlight key attributes, filter criteria, or represent user input in a concise form. Chips support truncation with maxWidth and can be combined with icons or actions for richer UI patterns.
 * Always use <Chip> from @fox/atoms for consistency with SwiftFox design language.
 */
export const Chip = forwardRef<HTMLDivElement, ExtendedChipProps>(({ maxWidth, disabled, center, ...props }, ref) => {
	return (
		<MuiChip
			{...props}
			style={{ overflow: 'ellipses', maxWidth, margin: center ? 'auto' : undefined }}
			ref={ref}
			deleteIcon={<ClearIcon style={{ fontSize: '1rem' }} />}
			disabled={disabled}

			// title={props.label}
		/>
	);
});

export const StatusChip = ({ maxWidth, disabled, backgroundColor, label, icon, center, title, ...props }: IStatusChip) => {
	const theme = useTheme();
	const hideTitle = title === false;

	return (
		<MuiChip
			{...props}
			title={hideTitle ? undefined : (title ?? label)}
			style={{
				overflow: 'ellipses',
				maxWidth,
				color: backgroundColor ? theme.palette[backgroundColor].dark : undefined,
				backgroundColor: backgroundColor ? alpha(theme.palette[backgroundColor].light, 0.1) : undefined,
				fontWeight: 400,
				margin: center ? 'auto' : undefined,
				...props.style
			}}
			disabled={disabled}
			label={label}
			sx={{
				'& .MuiSvgIcon-root': {
					color: backgroundColor ? theme.palette[backgroundColor].dark : undefined,
					paddingLeft: icon ? '0.25rem' : undefined
				}
			}}
			icon={icon}
		/>
	);
};

export const AddPersonIcon = () => {
	return <PersonAddIcon />;
};
export const NewPersonIcon = () => {
	return <AddCircleIcon />;
};
export const WarningPersonIcon = () => {
	return <WarningIcon />;
};
export const SuccessDataIcon = () => {
	return <CheckCircleIcon />;
};
export const WarningDataIcon = () => {
	return <CancelIcon />;
};

export const SkipIcon = () => {
	return <DoNotDisturbIcon />;
};

const SENTIMENT_ICONS = {
	Positive: 'Mood',
	Neutral: 'SentimentNeutral',
	Negative: 'MoodBad'
} as const;

const SENTIMENT_COLORS = {
	Positive: 'success',
	Neutral: 'info',
	Negative: 'error'
} as const;

export const SentimentChip = ({
	sentiment,
	title,
	loading,
	alignSelf,
	size = 'small'
}: {
	size?: ChipProps['size'];
	sentiment?: keyof typeof SENTIMENT_COLORS | null;
	title?: string;
	loading?: boolean;
	alignSelf?: 'start' | 'end';
}) => {
	if (loading) {
		return <Skeleton variant="text" width="10ch" />;
	}

	if (!sentiment) return null;

	return (
		<StatusChip
			size={size}
			backgroundColor={SENTIMENT_COLORS[sentiment]}
			style={{ alignSelf }}
			icon={<Icon fontSize="small" iconName={SENTIMENT_ICONS[sentiment]} />}
			label={sentiment}
			title={title ?? false}
		/>
	);
};
